import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table"
import { useState, useMemo, useRef, useEffect } from "react"
import { useTareas } from "../context/TareasContext"
import { PRIORITY_CONFIG } from "../context/TareasContext"
import AssigneeAvatar from "../components/AssigneeAvatar"
import Selector from "../../ui/components/Selector"
import { FaSort, FaSortUp, FaSortDown, FaPlus, FaExternalLinkAlt, FaUserPlus, FaTimes, FaExpand, FaColumns, FaTrash, FaExchangeAlt } from "react-icons/fa"
import IconButton from "@mui/material/IconButton"
import DeleteIcon from "@mui/icons-material/Delete"
import Pagination from "@mui/material/Pagination"
import CheckBox from "../../ui/components/CheckBox"
import formatWorkerDisplayName from "../utils/formatWorkerDisplayName"

const deltaToPlainText = (content, maxLen = 60) => {
  if (!content) return ""
  if (typeof content === "string") return content.trim().slice(0, maxLen) + (content.length > maxLen ? "…" : "")
  if (typeof content === "object" && content.ops && Array.isArray(content.ops)) {
    const text = content.ops
      .filter(op => typeof op.insert === "string")
      .map(op => op.insert)
      .join("")
      .replace(/\n/g, " ")
      .trim()
    return text.slice(0, maxLen) + (text.length > maxLen ? "…" : "")
  }
  return ""
}

const PRIORITIES = ["Baja", "Media", "Alta", "Urgente"]
const PRIORITY_ICONS = { Baja: "↓", Media: "→", Alta: "↑", Urgente: "⚡" }

// --- Celda título editable inline ---
const InlineTitleCell = ({ task, openTask }) => {
  const { updateTask, selectedProject } = useTareas()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(task.task)
  const inputRef = useRef(null)

  const commit = async () => {
    setEditing(false)
    const trimmed = value.trim()
    if (trimmed && trimmed !== task.task) await updateTask(task.id, { task: trimmed })
    else setValue(task.task)
  }

  if (editing) return (
    <input
      ref={inputRef}
      value={value}
      onChange={e => setValue(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === "Enter") commit()
        if (e.key === "Escape") { setEditing(false); setValue(task.task) }
      }}
      onClick={e => e.stopPropagation()}
      autoFocus
      className="w-full text-xs font-medium text-gray-800 border border-orange-300 rounded-lg px-1.5 py-0.5 focus:outline-none focus:ring-2 focus:ring-orange-300"
    />
  )

  return (
    <div className="flex flex-col gap-0.5 group/title">
      <div className="flex items-start gap-1.5">
        <span
          className="text-xs font-medium text-gray-800 cursor-text hover:text-orange-600 whitespace-normal break-words flex-1"
          onClick={e => { e.stopPropagation(); setValue(task.task); setEditing(true); setTimeout(() => inputRef.current?.select(), 0) }}
          title="Clic para editar"
        >
          {task.task}
        </span>
        <button
          onClick={e => { e.stopPropagation(); openTask(task) }}
          className="opacity-0 group-hover/title:opacity-100 flex-shrink-0 p-1 text-gray-400 hover:text-orange-500 hover:bg-orange-50 rounded transition-all"
          title="Abrir detalle"
        >
          <FaExpand className="text-[9px]" />
        </button>
      </div>
      {task.tipo?.name && <span className="text-[10px] text-gray-400">{task.tipo.name}</span>}
      {(() => {
        const defs = selectedProject?.tags || []
        const ids = (task.projectTags || []).map((x) => (typeof x === "object" && x != null ? x.tagId : x)).filter(Boolean)
        if (ids.length === 0) return null
        return (
          <div className="flex flex-wrap gap-0.5 mt-0.5">
            {ids.map((tid) => {
              const def = defs.find((t) => t.id === tid)
              const label = def?.name || tid.slice(0, 8)
              const col = def?.color || "#9CA3AF"
              return (
                <span
                  key={tid}
                  className="text-[9px] px-1 py-0 rounded border border-gray-100 max-w-[100px] truncate"
                  style={{ color: col, backgroundColor: `${col}18` }}
                  title={def?.archived ? `${label} (archivado)` : label}
                >
                  {label}
                </span>
              )
            })}
          </div>
        )
      })()}
    </div>
  )
}

// --- Celda prioridad con popover ---
const InlinePriorityCell = ({ task }) => {
  const { updateTask } = useTareas()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const config = task.priority ? (PRIORITY_CONFIG[task.priority] ?? { color: "#6B7280", bg: "#F3F4F6" }) : null

  return (
    <div ref={ref} className="relative" onClick={e => e.stopPropagation()}>
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium transition-colors hover:opacity-80"
        style={config ? { backgroundColor: config.bg, color: config.color } : { backgroundColor: "#F3F4F6", color: "#9CA3AF" }}
      >
        {task.priority ? <><span>{PRIORITY_ICONS[task.priority]}</span>{task.priority}</> : <span>—</span>}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-1.5 flex flex-col gap-0.5 min-w-[110px]">
          <button onClick={() => { updateTask(task.id, { priority: "" }); setOpen(false) }} className="text-left text-[10px] px-2 py-1 rounded-lg hover:bg-gray-50 text-gray-400">Sin prioridad</button>
          {PRIORITIES.map(p => {
            const cfg = PRIORITY_CONFIG[p]
            return (
              <button
                key={p}
                onClick={() => { updateTask(task.id, { priority: p }); setOpen(false) }}
                className="text-left text-[10px] px-2 py-1 rounded-lg flex items-center gap-1.5 font-medium"
                style={{ color: cfg.color, backgroundColor: task.priority === p ? cfg.bg : "transparent" }}
              >
                <span>{PRIORITY_ICONS[p]}</span>{p}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// --- Celda estado con popover ---
const InlineStatusCell = ({ task }) => {
  const { updateTask, getStatusId, getTaskStatusValue, estadosTareas } = useTareas()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const currentName = getTaskStatusValue(task)
  const current = estadosTareas.find(e => e.name === currentName)

  const handleSelect = async (estado) => {
    setOpen(false)
    const statusId = getStatusId(estado.name)
    if (statusId) await updateTask(task.id, { status: statusId })
  }

  return (
    <div ref={ref} className="relative" onClick={e => e.stopPropagation()}>
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-medium transition-all hover:opacity-80 border"
        style={current
          ? { backgroundColor: `${current.color}18`, color: current.color, borderColor: `${current.color}40` }
          : { backgroundColor: "#F3F4F6", color: "#9CA3AF", borderColor: "#E5E7EB" }
        }
      >
        {current && (
          <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: current.color }} />
        )}
        {currentName || "Sin estado"}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-1.5 flex flex-col gap-0.5 min-w-[160px]">
          {estadosTareas.map(e => (
            <button
              key={e.id}
              onClick={() => handleSelect(e)}
              className="flex items-center gap-2 text-[10px] px-2.5 py-1.5 rounded-lg transition-colors text-left font-medium"
              style={{
                color: e.color || "#374151",
                backgroundColor: currentName === e.name ? `${e.color}15` : "transparent",
              }}
              onMouseEnter={ev => ev.currentTarget.style.backgroundColor = `${e.color}18`}
              onMouseLeave={ev => ev.currentTarget.style.backgroundColor = currentName === e.name ? `${e.color}15` : "transparent"}
            >
              <span
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ backgroundColor: e.color || "#6B7280" }}
              />
              {e.name}
              {currentName === e.name && (
                <span className="ml-auto text-[9px] opacity-60">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// --- Celda asignados con add/remove ---
const InlineAssigneesCell = ({ task }) => {
  const { updateTask, workers } = useTareas()
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const assignees = task.assignees || []
  const assigneeIds = assignees.map(a => (typeof a === "object" ? a.id : a)).filter(Boolean)
  const available = workers.filter(w => !assigneeIds.includes(w.id))

  const add = async (workerId) => {
    if (!workerId) return
    await updateTask(task.id, { assignees: [...assigneeIds, workerId] })
    setOpen(false)
  }
  const remove = async (id) => {
    await updateTask(task.id, { assignees: assigneeIds.filter(x => x !== id) })
  }

  return (
    <div ref={ref} className="relative flex items-center gap-1 flex-wrap" onClick={e => e.stopPropagation()}>
      {assignees.map(a => {
        const id = typeof a === "object" ? a.id : a
        const name = typeof a === "object" ? a.name : workers.find(w => w.id === id)?.name || id
        return (
          <div key={id} className="group/av relative">
            <AssigneeAvatar name={name} size={22} user={typeof a === "object" ? a : workers.find(w => w.id === id)} />
            <button
              onClick={e => { e.stopPropagation(); remove(id) }}
              className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 text-white rounded-full items-center justify-center text-[7px] hidden group-hover/av:flex"
            >
              <FaTimes />
            </button>
          </div>
        )
      })}
      {available.length > 0 && (
        <>
          <button
            onClick={() => setOpen(v => !v)}
            className="w-5 h-5 rounded-full border border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-orange-400 hover:text-orange-400 transition-colors"
          >
            <FaUserPlus className="text-[8px]" />
          </button>
          {open && (
            <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-1.5 flex flex-col gap-0.5 min-w-[150px]">
              {available.map(w => (
                <button
                  key={w.id}
                  onClick={() => add(w.id)}
                  className="flex items-center gap-2 text-[10px] px-2 py-1.5 rounded-lg hover:bg-orange-50 text-gray-700"
                >
                  <AssigneeAvatar name={w.name} size={16} user={w} />
                  {formatWorkerDisplayName(w.name || w.email)}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

const formatDate = (date) => {
  if (!date) return "—"
  const d = new Date(date)
  const months = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"]
  return `${String(d.getDate()).padStart(2,"0")} ${months[d.getMonth()]} ${d.getFullYear()}`
}

const columnHelper = createColumnHelper()

const COLUMN_LABELS = {
  select:     "Selección",
  actions:    "Acciones",
  "createdBy_name": "Creado por",
  task:       "Tarea",
  priority:   "Prioridad",
  status:     "Estado",
  assignees:  "Asignados",
  adminNotes: "Notas admin",
  links:      "Links",
  limitDate:  "Fecha límite",
  deliverDate:"Entregado",
}

// --- Barra de acciones en lote ---
const BulkActionsBar = ({ selectedIds, onClear }) => {
  const { deleteTask, updateTask, getStatusId, estadosTareas } = useTareas()
  const [showStatus, setShowStatus] = useState(false)
  const [showPriority, setShowPriority] = useState(false)
  const statusRef = useRef(null)
  const priorityRef = useRef(null)

  useEffect(() => {
    const handler = (e) => {
      if (statusRef.current && !statusRef.current.contains(e.target)) setShowStatus(false)
      if (priorityRef.current && !priorityRef.current.contains(e.target)) setShowPriority(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const bulkDelete = async () => {
    const ok = window.confirm(`¿Eliminar ${selectedIds.length} tareas?`)
    if (!ok) return
    for (const id of selectedIds) await deleteTask(id)
    onClear()
  }

  const bulkStatus = async (statusName) => {
    const id = getStatusId(statusName)
    if (!id) return
    for (const taskId of selectedIds) await updateTask(taskId, { status: id })
    setShowStatus(false)
  }

  const bulkPriority = async (p) => {
    for (const taskId of selectedIds) await updateTask(taskId, { priority: p })
    setShowPriority(false)
  }

  return (
    <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-20 flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-2xl shadow-2xl text-xs ring-1 ring-white/10 animate-[bulkBarIn_0.18s_ease-out]">
      <style>{`
        @keyframes bulkBarIn {
          from { opacity: 0; transform: translate(-50%, 10px); }
          to   { opacity: 1; transform: translate(-50%, 0); }
        }
      `}</style>

      <span className="font-semibold text-orange-400 pr-1 border-r border-white/20 mr-1">
        {selectedIds.length} sel.
      </span>

      <div ref={statusRef} className="relative">
        <button
          onClick={() => setShowStatus(v => !v)}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
        >
          <FaExchangeAlt className="text-[9px]" /> Estado
        </button>
        {showStatus && (
          <div className="absolute bottom-full mb-2 left-0 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-1.5 min-w-[150px]">
            {estadosTareas.map(e => (
              <button
                key={e.id}
                onClick={() => bulkStatus(e.name)}
                className="flex items-center gap-2 text-[10px] px-2.5 py-1.5 rounded-lg hover:bg-gray-50 w-full text-left"
                style={{ color: e.color || "#374151" }}
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: e.color || "#6B7280" }} />
                {e.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div ref={priorityRef} className="relative">
        <button
          onClick={() => setShowPriority(v => !v)}
          className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
        >
          <FaExchangeAlt className="text-[9px]" /> Prioridad
        </button>
        {showPriority && (
          <div className="absolute bottom-full mb-2 left-0 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-1.5 min-w-[120px]">
            <button onClick={() => bulkPriority("")} className="text-[10px] px-2 py-1 rounded-lg hover:bg-gray-50 w-full text-left text-gray-400">Sin prioridad</button>
            {PRIORITIES.map(p => (
              <button
                key={p}
                onClick={() => bulkPriority(p)}
                className="flex items-center gap-1.5 text-[10px] px-2 py-1 rounded-lg hover:bg-gray-50 w-full text-left font-medium"
                style={{ color: PRIORITY_CONFIG[p].color }}
              >
                <span>{PRIORITY_ICONS[p]}</span>{p}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="w-px h-4 bg-white/20 mx-0.5" />

      <button
        onClick={bulkDelete}
        className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-red-500/20 hover:bg-red-500/40 text-red-300 hover:text-red-200 transition-colors"
      >
        <FaTrash className="text-[9px]" /> Eliminar
      </button>

      <button
        onClick={onClear}
        className="ml-1 p-1 text-white/40 hover:text-white/80 transition-colors"
        title="Cerrar"
      >
        <FaTimes className="text-[10px]" />
      </button>
    </div>
  )
}

const TablaView = () => {
  const {
    tasks, deleteTask, openTask,
    totalPages, totalDocs, page, limit,
    handlePageChange, handleLimitChange,
    setCreateModalOpen,
  } = useTareas()

  const [sorting, setSorting] = useState([])
  const [columnVisibility, setColumnVisibility] = useState({ limitDate: false, deliverDate: false })
  const [showColPicker, setShowColPicker] = useState(false)
  const [selectedIds, setSelectedIds] = useState([])
  const colPickerRef = useRef(null)

  const allIds = tasks.map(t => t.id)
  const allSelected = allIds.length > 0 && allIds.every(id => selectedIds.includes(id))
  const someSelected = selectedIds.length > 0 && !allSelected

  const toggleOne = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])
  const toggleAll = () => {
    if (allSelected) setSelectedIds([])
    else setSelectedIds([...allIds])
  }

  useEffect(() => {
    const handler = (e) => {
      if (colPickerRef.current && !colPickerRef.current.contains(e.target)) setShowColPicker(false)
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [])

  const columns = useMemo(() => [
    columnHelper.display({
      id: "select",
      header: () => (
        <CheckBox
          checked={allSelected}
          indeterminate={someSelected}
          onChange={toggleAll}
        />
      ),
      size: 36,
      cell: ({ row }) => (
        <div className="flex items-center justify-center">
          <CheckBox
            checked={selectedIds.includes(row.original.id)}
            onChange={() => toggleOne(row.original.id)}
            onClick={e => e.stopPropagation()}
          />
        </div>
      ),
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      size: 50,
      cell: ({ row }) => (
        <div className="flex items-center justify-center">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); deleteTask(row.original.id) }}>
            <DeleteIcon fontSize="inherit" sx={{ color: "#EF4444" }} />
          </IconButton>
        </div>
      ),
    }),
    columnHelper.accessor("createdBy.name", {
      header: "Creado por",
      size: 130,
      cell: ({ getValue, row }) => {
        const raw = getValue() || ""
        return (
          <div className="flex items-center gap-2">
            <AssigneeAvatar name={raw || "?"} size={24} user={row.original.createdBy} />
            <span className="truncate max-w-[90px]">{raw ? formatWorkerDisplayName(raw) : "—"}</span>
          </div>
        )
      },
    }),
    columnHelper.accessor("task", {
      header: "Tarea",
      size: 220,
      cell: ({ row }) => <InlineTitleCell task={row.original} openTask={openTask} />,
    }),
    columnHelper.accessor("priority", {
      header: "Prioridad",
      size: 110,
      cell: ({ row }) => <InlinePriorityCell task={row.original} />,
    }),
    columnHelper.display({
      id: "status",
      header: "Estado",
      size: 150,
      cell: ({ row }) => <InlineStatusCell task={row.original} />,
    }),
    columnHelper.display({
      id: "assignees",
      header: "Asignados",
      size: 180,
      cell: ({ row }) => <InlineAssigneesCell task={row.original} />,
    }),
    columnHelper.display({
      id: "adminNotes",
      header: "Notas admin",
      size: 200,
      cell: ({ row }) => {
        const content = row.original.adminNotes
        const preview = deltaToPlainText(content)
        return (
          <button
            onClick={e => { e.stopPropagation(); openTask(row.original) }}
            className="text-left w-full text-xs text-gray-600 hover:text-orange-600 truncate block max-w-full"
            title={preview || "Abrir para editar notas"}
          >
            {preview || <span className="text-gray-400 italic">Sin notas</span>}
          </button>
        )
      },
    }),
    columnHelper.display({
      id: "links",
      header: "Links",
      size: 80,
      cell: ({ row }) => {
        const adminLinks = row.original.adminLinks || []
        const workerLinks = row.original.workerLinks || []
        const total = adminLinks.length + workerLinks.length
        return total > 0 ? (
          <button
            onClick={e => { e.stopPropagation(); openTask(row.original) }}
            className="text-xs text-blue-600 hover:underline flex items-center gap-1"
          >
            <FaExternalLinkAlt className="text-[10px]" /> {total}
          </button>
        ) : (
          <span className="text-gray-300 text-xs">—</span>
        )
      },
    }),
    columnHelper.accessor("limitDate", {
      header: "Fecha límite",
      size: 120,
      cell: ({ getValue }) => {
        const date = getValue()
        if (!date) return <span className="text-gray-300">—</span>
        const isPast = new Date(date) < new Date()
        return (
          <span className={`text-xs ${isPast ? "text-red-500 font-medium" : "text-gray-600"}`}>
            {formatDate(date)}
          </span>
        )
      },
    }),
    columnHelper.accessor("deliverDate", {
      header: "Entregado",
      size: 120,
      cell: ({ getValue }) => (
        <span className="text-xs text-gray-600">{formatDate(getValue())}</span>
      ),
    }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [openTask, deleteTask, selectedIds, allSelected, someSelected])

  const table = useReactTable({
    data: tasks,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: totalPages,
  })

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto relative">
        <table className="w-full border-collapse">
          <thead className="sticky top-0 z-10">
            {table.getHeaderGroups().map(hg => (
              <tr key={hg.id}>
                {hg.headers.map(header => (
                  <th
                    key={header.id}
                    style={{ width: header.getSize() }}
                    className="bg-[#FE6400] text-white text-xs font-semibold px-3 py-2.5 text-left border-r border-orange-400 last:border-r-0 select-none whitespace-nowrap"
                  >
                    <div
                      className={`flex items-center gap-1 ${header.column.getCanSort() ? "cursor-pointer" : ""}`}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {header.column.getCanSort() && (
                        <span className="text-orange-200">
                          {header.column.getIsSorted() === "asc" ? <FaSortUp /> :
                           header.column.getIsSorted() === "desc" ? <FaSortDown /> :
                           <FaSort className="opacity-50" />}
                        </span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row, i) => (
              <tr
                key={row.id}
                className={`text-xs border-b border-gray-100 transition-colors group ${
                  i % 2 === 0 ? "bg-white" : "bg-gray-50/50"
                } hover:bg-orange-50/30`}
              >
                {row.getVisibleCells().map(cell => (
                  <td
                    key={cell.id}
                    style={{ width: cell.column.getSize() }}
                    className="px-3 py-2 border-r border-gray-100 last:border-r-0 align-middle"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}

            {/* Fila de añadir nueva tarea */}
            <tr className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
              <td colSpan={columns.length} className="px-3 py-2">
                <button
                  onClick={() => setCreateModalOpen(true)}
                  className="flex items-center gap-2 text-xs text-gray-400 hover:text-orange-500 transition-colors w-full"
                >
                  <FaPlus className="text-[10px]" />
                  Nueva tarea
                </button>
              </td>
            </tr>
          </tbody>
        </table>

        {tasks.length === 0 && (
          <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
            No hay tareas que mostrar
          </div>
        )}

        {selectedIds.length > 0 && (
          <BulkActionsBar selectedIds={selectedIds} onClear={() => setSelectedIds([])} />
        )}
      </div>

      {/* Paginación */}
      <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-white flex-shrink-0">
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">
            Mostrando <strong>{tasks.length}</strong> de <strong>{totalDocs}</strong> tareas
          </span>
          <Selector
            title="Mostrar"
            value={limit}
            setter={(val) => handleLimitChange(Number(val))}
            options={[15, 25, 35, 50].map(n => ({ value: n, label: `${n} por página` }))}
            width="w-[8rem]"
          />

          {/* Selector de columnas */}
          <div ref={colPickerRef} className="relative">
            <button
              onClick={() => setShowColPicker(v => !v)}
              className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-lg border transition-colors ${
                showColPicker ? "border-orange-400 bg-orange-50 text-orange-600" : "border-gray-200 text-gray-500 hover:bg-gray-50"
              }`}
            >
              <FaColumns className="text-[11px]" />
              Columnas
            </button>
            {showColPicker && (
              <div className="absolute bottom-full mb-2 left-0 bg-white border border-gray-200 rounded-xl shadow-lg z-50 p-2 min-w-[170px]">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2 px-1">Mostrar columnas</p>
                {table.getAllLeafColumns().map(col => {
                  const label = COLUMN_LABELS[col.id] || col.id
                  return (
                    <label
                      key={col.id}
                      className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={col.getIsVisible()}
                        onChange={col.getToggleVisibilityHandler()}
                        className="accent-orange-500 w-3 h-3"
                      />
                      <span className="text-xs text-gray-700">{label}</span>
                    </label>
                  )
                })}
              </div>
            )}
          </div>
        </div>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          variant="outlined"
          shape="rounded"
          size="small"
        />
      </div>
    </div>
  )
}

export default TablaView
